import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import HomeItem from "./HomeItem";
import AssetsItem from "./AssetsItem";
import ManagementCentreItem from "./ManagementCentreItem";
import SettingsItem from "./SettingsItem";
import LoggedUserItem from "./LoggedUserItem";
import FinanceItem from "./FinanceItem";
import { SUBMENU } from "../data/contants";
import ArrowIcon from "../../../Common/Svgs/ArrowIcon";
import OperationItem from "./OperationItem";
import usePermission from "../../hooks/usePermission";
import { PERMISSIONS } from "../../../Modules/Login/constants";
import EnergyManagementItem from "./EnergyManagementItem";

const Sidebar = ({ pageName, sidebarActive, setSidebarActive }) => {
  const { companyLogo } = useSelector((state) => state.loggedInUser);

  const [subMenu, setSubMenu] = useState(SUBMENU.DEFAULT);

  const sidebarRef = useRef();

  // show/hide the main option based on the permissions
  const hasAssetPermission = usePermission([
    PERMISSIONS.TRIKE_VIEW,
    PERMISSIONS.TRIKE_VIEW_AND_EDIT,
    PERMISSIONS.POWER_AFRICA_ASSET_VIEW,
    PERMISSIONS.POWER_AFRICA_ASSET_VIEW_AND_EDIT,
  ]);

  const hasEnergyManagementPermission = usePermission([
    PERMISSIONS.BATTERY_VIEW,
    PERMISSIONS.BATTERY_VIEW_AND_EDIT,
    PERMISSIONS.CHARGER_VIEW,
    PERMISSIONS.CHARGER_VIEW_AND_EDIT,
  ]);

  const hasOperationPermission = usePermission([
    PERMISSIONS.TRIP_VIEW,
    PERMISSIONS.TRIP_VIEW_AND_EDIT,
    PERMISSIONS.SESSION_VIEW,
    PERMISSIONS.SESSION_VIEW_AND_EDIT,
    PERMISSIONS.RENTAL_VIEW,
    PERMISSIONS.RENTAL_VIEW_AND_EDIT,
  ]);

  const hasManagementCentrePermission = usePermission([
    PERMISSIONS.DRIVER_VIEW,
    PERMISSIONS.DRIVER_VIEW_AND_EDIT,
    PERMISSIONS.PARTNER_VIEW,
    PERMISSIONS.PARTNER_VIEW_AND_EDIT,
    PERMISSIONS.LOCATIONS_VIEW,
    PERMISSIONS.LOCATION_VIEW_AND_EDIT,
    PERMISSIONS.SERVICE_VIEW,
    PERMISSIONS.SERVICE_VIEW_AND_EDIT,
    PERMISSIONS.TRIKE_VIEW_QR,
    PERMISSIONS.BATTERY_VIEW_QR,
    PERMISSIONS.PARTNER_VIEW_QR,
    PERMISSIONS.REQUEST_VIEW,
    PERMISSIONS.REQUEST_VIEW_AND_EDIT,
  ]);

  const hasFinancePermission = usePermission([
    PERMISSIONS.REVENUE_VIEW,
    PERMISSIONS.REVENUE_VIEW_AND_EDIT,
    PERMISSIONS.RATE_VIEW,
    PERMISSIONS.RATE_VIEW_AND_EDIT,
  ]);

  const handleMenuClick = () => {
    setSidebarActive((prev) => !prev);
  };

  // set the submenu to default if user clicks anywhere outside the sidebar container
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSubMenu(SUBMENU.DEFAULT);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <aside
      ref={sidebarRef}
      className="absolute z-[99999] h-screen pt-6 flex flex-col bg-primary border-r border-light-grey transition-all duration-200"
      style={{ width: sidebarActive ? "232px" : "72px" }}
    >
      <div className="flex items-center justify-between px-5">
        <div
          className="w-full"
          role="button"
          onClick={() => {
            if (!sidebarActive) {
              handleMenuClick();
            }
          }}
        >
          <img
            src={sidebarActive ? companyLogo : "/assets/menu.png"}
            className="w-8 h-8 shrink-0"
            alt="Songa Mobility"
          />
        </div>

        {sidebarActive && (
          <button
            type="button"
            className="-rotate-90"
            onClick={() => setSidebarActive(false)}
          >
            <ArrowIcon size={20} className="text-sidebar-base" />
          </button>
        )}
      </div>

      <div className="w-full">
        <HomeItem
          sidebarActive={sidebarActive}
          pageName={pageName}
          subMenu={subMenu}
        />

        {hasAssetPermission && (
          <AssetsItem
            sidebarActive={sidebarActive}
            pageName={pageName}
            subMenu={subMenu}
          />
        )}

        {hasOperationPermission && (
          <OperationItem
            sidebarActive={sidebarActive}
            setSidebarActive={setSidebarActive}
            pageName={pageName}
            subMenu={subMenu}
            setSubMenu={setSubMenu}
          />
        )}

        {hasEnergyManagementPermission && (
          <EnergyManagementItem
            sidebarActive={sidebarActive}
            setSidebarActive={setSidebarActive}
            pageName={pageName}
            subMenu={subMenu}
            setSubMenu={setSubMenu}
          />
        )}

        {hasManagementCentrePermission && (
          <ManagementCentreItem
            sidebarActive={sidebarActive}
            setSidebarActive={setSidebarActive}
            pageName={pageName}
            subMenu={subMenu}
            setSubMenu={setSubMenu}
          />
        )}

        <div className="mt-4 bg-primary-50 w-full h-[1px]" />

        {hasFinancePermission && (
          <FinanceItem
            sidebarActive={sidebarActive}
            setSidebarActive={setSidebarActive}
            subMenu={subMenu}
            setSubMenu={setSubMenu}
          />
        )}

        <SettingsItem
          sidebarActive={sidebarActive}
          pageName={pageName}
          subMenu={subMenu}
        />

        <LoggedUserItem
          sidebarActive={sidebarActive}
          setSidebarActive={setSidebarActive}
        />
      </div>
    </aside>
  );
};

export default Sidebar;
